import { memo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { SDropdownMenu } from '@setvi/shared/components';
import { Resource } from '@setvi/shared/interfaces';

import {
  usePrintOrderCartAction,
  usePrintOrderEnabled
} from 'componentsV2/print-order';
import { getResourceV2Query } from 'Services';

import { ActionProps, DropdownItemsType, useDropdownResource } from './hooks';

interface DropdownMenuProps {
  item: Resource;
  // ! This will be removed in the future
  newDesign?: boolean;
  dropdownItems?: DropdownItemsType;
  refetchLoading?: boolean;
  onAction?: ({ type, resource }: ActionProps) => void;
  handleCloseDropdown?: () => void | null;
}

const DropdownMenu = ({
  item,
  newDesign,
  dropdownItems,
  refetchLoading,
  onAction,
  handleCloseDropdown
}: DropdownMenuProps) => {
  const { createDropdownMenu } = useDropdownResource(
    refetchLoading,
    onAction,
    newDesign
  );

  const [opened, setOpened] = useState(false);
  const printOrderEnabled = usePrintOrderEnabled();

  // Global search hits come from Algolia without the print-order fields, and
  // the cart item needs them to know whether the asset can be added and which
  // customization dialog it opens. Resolved when the menu opens, so listing
  // resources costs nothing; the item waits rather than offering an action we
  // cannot judge yet.
  const needsCartFields =
    printOrderEnabled && !!item?.ID && item?.EnableAddToCart === undefined;

  const { data: resolved } = useQuery<Resource>({
    ...getResourceV2Query(item?.ID ?? 0),
    enabled: opened && needsCartFields
  });

  // Merged, not swapped: the endpoint does not return everything a caller
  // holds (an Algolia hit carries Breadcrumbs, which Go to Category reads).
  const resource = { ...item, ...resolved } as Resource;

  const cartAction = usePrintOrderCartAction(resource, {
    busy: needsCartFields && !resolved,
    onDone: handleCloseDropdown
  });

  return (
    <SDropdownMenu
      newDesign={newDesign}
      onOpen={() => setOpened(true)}
      menuItems={createDropdownMenu(
        resource,
        dropdownItems,
        handleCloseDropdown,
        cartAction
      )}
    />
  );
};

export default memo(DropdownMenu);
